import { useEffect, useRef } from "react";
import { api } from "./api";

/**
 * useLocationTracker — continuous client geo-tracking.
 *
 * Session 48 (2026-06-07): The login flow captures one-shot location, but
 * after that the admin had no way to see where the client physically is.
 * This hook runs while the client is logged in and pings POST /api/me/location
 * with the latest navigator.geolocation reading.
 *
 * Battery-friendly throttling:
 *   - Polls navigator.geolocation.getCurrentPosition() every `intervalMs` (default 60s)
 *   - POSTs to backend ONLY if moved > `minMoveMeters` (default 50m) OR
 *     more than `forceEverySec` (default 600s = 10min) since last successful send.
 *     This dramatically reduces DB writes for stationary clients.
 *
 * Permission handling:
 *   - First call relies on browser's existing geolocation permission from login.
 *   - If user denies → hook becomes a no-op silently (no permission re-prompts).
 *   - If browser doesn't support → no-op.
 *
 * NOT mounted for admin role — only client-side MobileLayout.
 */
export function useLocationTracker({
  enabled = true,
  intervalMs = 60_000,
  minMoveMeters = 50,
  forceEverySec = 600,
} = {}) {
  const lastSent = useRef(null);   // {lat, lng, atMs}
  const denied = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) return;

    let stopped = false;

    const haversine = (a, b) => {
      const R = 6371000;  // metres
      const toRad = (x) => (x * Math.PI) / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const s1 = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(s1));
    };

    const probe = () => {
      if (stopped || denied.current) return;
      navigator.geolocation.getCurrentPosition(
        async (p) => {
          if (stopped) return;
          const lat = p.coords.latitude;
          const lng = p.coords.longitude;
          const accuracy = p.coords.accuracy;
          const now = Date.now();

          const prev = lastSent.current;
          const distance = prev ? haversine(prev, { lat, lng }) : Infinity;
          const ageSec = prev ? (now - prev.atMs) / 1000 : Infinity;

          if (distance < minMoveMeters && ageSec < forceEverySec) {
            return;  // skip — stationary client
          }
          try {
            await api.post("/me/location", {
              latitude: lat, longitude: lng, accuracy,
            });
            lastSent.current = { lat, lng, atMs: now };
          } catch {
            // network failure — silent; will retry next tick
          }
        },
        (err) => {
          // PERMISSION_DENIED = 1, POSITION_UNAVAILABLE = 2, TIMEOUT = 3
          if (err && err.code === 1) denied.current = true;
        },
        { enableHighAccuracy: false, maximumAge: 30_000, timeout: 15_000 }
      );
    };

    // Initial probe shortly after mount (don't fight the login burst)
    const initId = setTimeout(probe, 5_000);
    const tickId = setInterval(probe, intervalMs);
    return () => {
      stopped = true;
      clearTimeout(initId);
      clearInterval(tickId);
    };
  }, [enabled, intervalMs, minMoveMeters, forceEverySec]);
}
